/**
 * SpecializationDetailPage — /admin/specializations/:id
 *
 * Three-tab detail view for a specialization:
 *   - Overview: name, shortCode, description, doctor/template counts, status
 *   - Vocabulary: medical glossary management (VocabularyTab)
 *   - Templates: (stub — future implementation)
 *
 * Pattern: page wraps itself in AdminLayout (same as F-508 pages).
 * Router has no layout wrapper for this route.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/layouts/AdminLayout';
import { api } from '@/lib/api';
import { VocabularyTab } from './VocabularyTab';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SpecializationDetail {
  id: string;
  name: string;
  shortCode: string;
  description: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
  doctorCount: number;
  templateCount: number;
  activeVocabulary: {
    id: string;
    versionNumber: number;
    termCount: number;
    activatedAt: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'ACTIVE' | 'INACTIVE' }) {
  if (status === 'ACTIVE') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
      Inactive
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'vocabulary' | 'templates';

interface TabBarProps {
  active: Tab;
  onChange: (tab: Tab) => void;
}

function TabBar({ active, onChange }: TabBarProps) {
  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'vocabulary', label: 'Vocabulary' },
    { id: 'templates', label: 'Templates' },
  ];

  return (
    <div className="flex border-b border-clinical-border mb-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`px-4 py-2.5 text-sm font-medium font-body border-b-2 transition-colors -mb-px ${
            active === tab.id
              ? 'border-clinical-teal text-clinical-teal'
              : 'border-transparent text-clinical-text-muted hover:text-clinical-charcoal hover:border-gray-300'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

interface OverviewTabProps {
  spec: SpecializationDetail;
  onActivate: () => void;
  onDeactivate: () => void;
  isActivating: boolean;
  isDeactivating: boolean;
}

function OverviewTab({ spec, onActivate, onDeactivate, isActivating, isDeactivating }: OverviewTabProps) {
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Left: Details */}
      <div className="clinical-card p-5 space-y-4">
        <h3 className="font-heading text-sm font-semibold text-clinical-charcoal uppercase tracking-wide">Details</h3>

        <div>
          <p className="text-xs font-body text-clinical-text-muted mb-0.5">Name</p>
          <p className="text-sm font-body font-medium text-clinical-charcoal">{spec.name}</p>
        </div>

        <div>
          <p className="text-xs font-body text-clinical-text-muted mb-0.5">Short Code</p>
          <span className="font-mono text-sm bg-clinical-cream-dark text-clinical-charcoal px-2 py-0.5 rounded">
            {spec.shortCode}
          </span>
        </div>

        {spec.description && (
          <div>
            <p className="text-xs font-body text-clinical-text-muted mb-0.5">Description</p>
            <p className="text-sm font-body text-clinical-charcoal">{spec.description}</p>
          </div>
        )}

        <div>
          <p className="text-xs font-body text-clinical-text-muted mb-0.5">Status</p>
          <StatusBadge status={spec.status} />
        </div>

        <div>
          <p className="text-xs font-body text-clinical-text-muted mb-0.5">Created</p>
          <p className="text-sm font-body text-clinical-charcoal">{formatDate(spec.createdAt)}</p>
        </div>
      </div>

      {/* Right: Stats + Actions */}
      <div className="space-y-4">
        {/* Stats */}
        <div className="clinical-card p-5">
          <h3 className="font-heading text-sm font-semibold text-clinical-charcoal uppercase tracking-wide mb-4">Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-heading font-bold text-clinical-charcoal">{spec.doctorCount}</p>
              <p className="text-xs font-body text-clinical-text-muted">Doctors</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-heading font-bold text-clinical-charcoal">{spec.templateCount}</p>
              <p className="text-xs font-body text-clinical-text-muted">Template Versions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-heading font-bold text-clinical-charcoal">
                {spec.activeVocabulary ? spec.activeVocabulary.termCount : '—'}
              </p>
              <p className="text-xs font-body text-clinical-text-muted">Vocabulary Terms</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-heading font-bold text-clinical-charcoal">
                {spec.activeVocabulary ? `v${spec.activeVocabulary.versionNumber}` : '—'}
              </p>
              <p className="text-xs font-body text-clinical-text-muted">Active Vocab Version</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="clinical-card p-5">
          <h3 className="font-heading text-sm font-semibold text-clinical-charcoal uppercase tracking-wide mb-4">Actions</h3>
          <div className="space-y-2">
            {spec.status === 'ACTIVE' ? (
              <button
                type="button"
                onClick={onDeactivate}
                disabled={isDeactivating}
                className="w-full text-sm font-body font-medium text-red-600 border border-red-200 rounded-clinical px-4 py-2 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {isDeactivating ? 'Deactivating…' : 'Deactivate Specialization'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onActivate}
                disabled={isActivating}
                className="w-full text-sm font-body font-medium text-clinical-teal border border-teal-200 rounded-clinical px-4 py-2 hover:bg-teal-50 transition-colors disabled:opacity-50"
              >
                {isActivating ? 'Activating…' : 'Activate Specialization'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplatesTab (stub)
// ---------------------------------------------------------------------------

function TemplatesTab({ spec }: { spec: SpecializationDetail }) {
  return (
    <div className="clinical-card p-6 text-center">
      <p className="text-sm font-body text-clinical-text-muted">
        Template management for <strong>{spec.name}</strong> — coming soon.
      </p>
      <p className="text-xs font-body text-clinical-text-muted mt-1">
        {spec.templateCount} template versions currently associated.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SpecializationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const { data, isLoading, error } = useQuery<{ data: SpecializationDetail }>({
    queryKey: ['admin', 'specializations', id],
    queryFn: () => api.get<{ data: SpecializationDetail }>(`/admin/specializations/${id}`),
    enabled: Boolean(id),
  });

  const spec = data?.data;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'specializations', id] });
  };

  const activateMutation = useMutation({
    mutationFn: () => api.post(`/admin/specializations/${id}/activate`),
    onSuccess: invalidate,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.post(`/admin/specializations/${id}/deactivate`),
    onSuccess: invalidate,
  });

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout breadcrumbs={[{ label: 'Specializations', to: '/admin/specializations' }, { label: '…' }]}>
        <div className="space-y-4">
          <div className="skeleton h-8 w-64 rounded" />
          <div className="skeleton h-4 w-40 rounded" />
          <div className="skeleton h-64 w-full rounded" />
        </div>
      </AdminLayout>
    );
  }

  // Error or not found
  if (error || !spec) {
    return (
      <AdminLayout breadcrumbs={[{ label: 'Specializations', to: '/admin/specializations' }, { label: 'Not Found' }]}>
        <div className="clinical-card p-8 text-center">
          <p className="text-sm font-body text-clinical-text-muted mb-4">
            {error ? 'Failed to load specialization.' : 'Specialization not found.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/admin/specializations')}
            className="btn-secondary text-sm px-4 py-2"
          >
            Back to Specializations
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      breadcrumbs={[
        { label: 'Specializations', to: '/admin/specializations' },
        { label: spec.name },
      ]}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">{spec.name}</h1>
            <span className="font-mono text-sm bg-clinical-cream-dark text-clinical-charcoal px-2 py-0.5 rounded">
              {spec.shortCode}
            </span>
            <StatusBadge status={spec.status} />
          </div>
          {spec.description && (
            <p className="text-sm font-body text-clinical-text-muted">{spec.description}</p>
          )}
        </div>

        <Link
          to="/admin/specializations"
          className="btn-secondary text-sm px-3 py-2 flex items-center gap-1.5 flex-none"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back
        </Link>
      </div>

      {/* Tabs */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          spec={spec}
          onActivate={() => activateMutation.mutate()}
          onDeactivate={() => deactivateMutation.mutate()}
          isActivating={activateMutation.isPending}
          isDeactivating={deactivateMutation.isPending}
        />
      )}

      {activeTab === 'vocabulary' && (
        <VocabularyTab
          specializationId={spec.id}
          activeVocabulary={spec.activeVocabulary}
          onRefresh={invalidate}
        />
      )}

      {activeTab === 'templates' && (
        <TemplatesTab spec={spec} />
      )}
    </AdminLayout>
  );
}
